import React from 'react';
import { Link } from 'react-router-dom';

const LOGO = '/logo.png';
const APP_NAME = 'PlayMaster IA';
const CONTACT_EMAIL = 'playmasteria@gmail.com';
const DOMAIN = 'playmasteria.com';
const LAST_UPDATED = '15 de junio de 2026';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-navy text-white">
      <header className="border-b border-gold/20 px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO} alt={APP_NAME} className="h-10" />
          <span className="font-h uppercase tracking-widest text-sm text-gold">{APP_NAME}</span>
        </Link>
        <nav className="text-xs flex gap-4">
          <Link to="/terms" className="text-gold hover:underline">Términos</Link>
          <Link to="/" className="text-gray-400 hover:text-white">Inicio</Link>
        </nav>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="page-privacy">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">Legal</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl mb-2">Política de Privacidad</h1>
        <p className="text-xs text-gray-400 mb-8">Última actualización: {LAST_UPDATED}</p>

        <div className="prose prose-invert max-w-none space-y-6 text-sm leading-relaxed text-gray-200">
          <p>
            En <strong>{APP_NAME}</strong> respetamos tu privacidad. Esta política explica qué datos recogemos,
            cómo los usamos, con quién los compartimos y qué derechos tienes sobre ellos.
            Al utilizar nuestros servicios (sitio web, aplicación PWA y APIs de <strong>{DOMAIN}</strong>) aceptas esta política.
          </p>

          <Section title="1. Datos que recogemos">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Datos de cuenta</strong>: nombre, email, contraseña hash (bcrypt), idioma, foto de perfil opcional, rol (coach / jugador / admin).</li>
              <li><strong>Datos de uso</strong>: jugadas creadas, rosters, invitaciones generadas, clics en enlaces de invitación, compras del marketplace y métricas agregadas de uso.</li>
              <li><strong>Datos técnicos</strong>: dirección IP, tipo de dispositivo, navegador, sistema operativo, cookies de sesión (httpOnly JWT) e identificadores anónimos de analytics.</li>
              <li><strong>Contenido subido</strong>: fotos de jugadores, descripciones de jugadas, audios de narración y archivos asociados al roster.</li>
              <li><strong>Datos de Google Sign-In</strong> (si lo usas): nombre, email verificado y foto de perfil pública, recibidos directamente del token de Google. No almacenamos tu contraseña de Google.</li>
            </ul>
          </Section>

          <Section title="2. Cómo usamos tus datos">
            <ul className="list-disc pl-5 space-y-2">
              <li>Proveer las funcionalidades del producto (autenticación, editor de jugadas, marketplace, IA Coach, animaciones).</li>
              <li>Personalizar tu experiencia (idioma, recomendaciones, recordatorios).</li>
              <li>Enviar correos transaccionales (verificación, recibos de compra, invitaciones de equipo) vía Resend.</li>
              <li>Procesar pagos y comisiones del marketplace.</li>
              <li>Mejorar el producto mediante analíticas agregadas y anónimas.</li>
              <li>Cumplir obligaciones legales (impuestos, prevención de fraude, requerimientos de autoridades).</li>
            </ul>
          </Section>

          <Section title="3. Base legal (RGPD / equivalente)">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Ejecución del contrato</strong>: cuando registras una cuenta y usas el servicio.</li>
              <li><strong>Consentimiento</strong>: cookies de analíticas, notificaciones push, emails de marketing.</li>
              <li><strong>Interés legítimo</strong>: seguridad, prevención de fraude, mejora del producto.</li>
              <li><strong>Obligación legal</strong>: facturación, retención fiscal, cumplimiento.</li>
            </ul>
          </Section>

          <Section title="4. Compartición con terceros">
            <p>Sólo compartimos datos con proveedores estrictamente necesarios para operar el servicio:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Google Sign-In</strong> — autenticación social.</li>
              <li><strong>Resend</strong> — entrega de correos transaccionales.</li>
              <li><strong>Stripe</strong> — pagos del marketplace y suscripciones.</li>
              <li><strong>Emergent AI</strong> — proveedor de IA (Claude / Gemini / OpenAI) para el IA Coach y generación de jugadas.</li>
              <li><strong>MongoDB Atlas / Emergent Storage</strong> — base de datos y almacenamiento de fotos.</li>
              <li><strong>PostHog</strong> — analíticas anónimas de producto.</li>
            </ul>
            <p className="mt-3">No vendemos tus datos personales a terceros.</p>
          </Section>

          <Section title="5. Cookies y tecnologías similares">
            <p>Usamos cookies estrictamente necesarias (sesión httpOnly JWT) y cookies opcionales de analíticas. Puedes bloquear cookies opcionales en cualquier momento desde tu navegador.</p>
          </Section>

          <Section title="6. Retención">
            <p>Conservamos tu cuenta y datos asociados mientras tu cuenta esté activa. Si eliminas tu cuenta, borramos tus datos personales en un máximo de <strong>30 días</strong>, salvo aquellos que debamos retener por obligación legal (facturas, durante el período fiscal aplicable).</p>
          </Section>

          <Section title="7. Seguridad">
            <p>Aplicamos cifrado en tránsito (TLS 1.2+), hashing de contraseñas con bcrypt, tokens JWT firmados, almacenamiento en infraestructura con acceso restringido y auditorías periódicas. No podemos garantizar seguridad absoluta — te recomendamos contraseñas únicas y no compartirlas.</p>
          </Section>

          <Section title="8. Tus derechos">
            <ul className="list-disc pl-5 space-y-2">
              <li>Acceso, rectificación y portabilidad de tus datos.</li>
              <li>Supresión (&ldquo;derecho al olvido&rdquo;) y limitación del tratamiento.</li>
              <li>Oposición al tratamiento basado en interés legítimo.</li>
              <li>Retirada del consentimiento en cualquier momento.</li>
              <li>Reclamación ante la autoridad de protección de datos competente (AEPD en España, INAI en México, ANPD en Brasil, etc.).</li>
            </ul>
            <p className="mt-3">Para ejercer tus derechos escríbenos a <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
          </Section>

          <Section title="9. Menores">
            <p>El servicio no está dirigido a menores de 13 años. Si eres menor de 16 (o la edad mínima de tu jurisdicción), necesitas el consentimiento de tus padres o tutores. Si detectamos datos de menores recogidos sin consentimiento, los borraremos.</p>
          </Section>

          <Section title="10. Transferencias internacionales">
            <p>Tus datos pueden almacenarse en servidores fuera de tu país. Garantizamos que se aplican salvaguardas adecuadas (cláusulas contractuales tipo de la UE) con todos nuestros proveedores.</p>
          </Section>

          <Section title="11. Cambios en esta política">
            <p>Si modificamos esta política te notificaremos por email o mediante un aviso destacado en la app al menos 7 días antes de que entre en vigor.</p>
          </Section>

          <Section title="12. Contacto">
            <p>Responsable del tratamiento: <strong>{APP_NAME}</strong>. Email: <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Sitio web: <a className="text-gold underline" href={`https://${DOMAIN}`}>https://{DOMAIN}</a>.</p>
          </Section>
        </div>

        <div className="mt-12 pt-6 border-t border-gold/20 text-xs text-gray-400 flex justify-between flex-wrap gap-3">
          <Link to="/terms" className="text-gold hover:underline">Términos del servicio</Link>
          <Link to="/" className="text-gold hover:underline">Volver al inicio</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-h uppercase text-lg sm:text-xl text-gold mt-6 mb-2">{title}</h2>
      <div className="text-gray-200">{children}</div>
    </section>
  );
}
